import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import useAdvanceStatements, {
  useAdvanceStatementsProps,
} from 'features/advance/hooks/useAdvanceStatements.hook';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import SectionedContentScreen from 'shared/containers/vxl/SectionedContentScreen/SectionedContentScreen';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import EmptyStateScreen from 'shared/patterns/vxl/EmptyStateScreen/EmptyStateScreen';
import ErrorScreen from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

const YearlyStatement = () => {
  const router = useRouter();
  const { id } = useLocalSearchParams<{ id: string }>();
  const { t: translatedText } = useTranslation('advance', {
    keyPrefix: 'advanceDetails',
  });

  const props: useAdvanceStatementsProps = {
    id: id,
  };
  const { data, isNotEmpty, hasActiveAdvance, loading, errorModel } =
    useAdvanceStatements(props);

  const handleAction = () => {
    router.push({
      pathname: '/accounts/advance-details',
      params: {
        id,
      },
    });
  };

  useViewDidFocusScreenAnalyticsEvent({
    key: '/accounts/advance-details/statements/docs',
    customAttributes: { account_type: 'advance' },
  });

  return (
    <View style={styles.screen}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: 'Varo Bank Account',
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />

      {isNotEmpty && data?.sections?.length > 0 ? (
        <SectionedContentScreen model={data} />
      ) : (
        <EmptyStateScreen
          bodyText={translatedText('listItems.noHistoryBody')}
          headlineText={translatedText('listItems.noStatements')}
          illustrationKey="CASH_A"
          actionButtonText={
            !hasActiveAdvance
              ? translatedText('listItems.noHistoryButtonText')
              : undefined
          }
          actionOnPress={!hasActiveAdvance ? handleAction : undefined}
        />
      )}

      <ScreenTransitionOverlay
        isLoading={loading}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          errorModel && !loading ? <ErrorScreen {...errorModel} /> : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  screen: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    paddingVertical: theme.spacing.m,
  },
});

export default YearlyStatement;
